// oRPC client typed against the backend's AppRouter — Rule 6 in action.
// The studio never declares an ApiResponse interface; types flow from the
// backend's router definition via `import type { AppRouter }`.

import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@chitos/backend/dist/app.js';

/**
 * Resolve the backend base URL at runtime from `window.location.hostname`.
 * URL IS the tenant key — each tenant's UI lives at <sub>.<apex> and calls
 * its OWN API host at <sub>-api.<apex>. The master tenant (chitos.<apex>)
 * follows the same pattern — its API is chitos-api.<apex>.
 *
 * Examples:
 *   suppuchits.chitdesk.com  → https://suppuchits-api.chitdesk.com/api
 *   chitos.chitdesk.com      → https://chitos-api.chitdesk.com/api
 *   localhost (Vite :3000)       → http://localhost:4000/api (local dev)
 *
 * Build-time override (`VITE_BACKEND_URL`) takes precedence for one-off
 * deploys where the URL convention doesn't apply (e.g. a preview env).
 */
function resolve_backend_url(): string {
  const baked = import.meta.env.VITE_BACKEND_URL;
  if (baked && baked.length > 0) {
    return `${baked.replace(/\/+$/, '')}/api`;
  }
  if (typeof window === 'undefined') return '/api';

  const host = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;

  // Local dev — backend always at localhost:4000
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return 'http://localhost:4000/api';
  }

  // Domain-agnostic derivation: the API host is always `<sub>-api.<apex>`,
  // and the apex is whatever's left after the first label. This lets ONE
  // studio bundle serve every domain we host (chitdesk.com, chitdesk.com,
  // future custom domains) without baking an apex in at build time.
  //
  //   chitos.chitdesk.com       → chitos-api.chitdesk.com
  //   test17.chitdesk.com   → test17-api.chitdesk.com
  //   chitdesk.com (bare apex)  → chitos-api.chitdesk.com   (master login)
  //   www.chitdesk.com          → chitos-api.chitdesk.com   (master login)
  const parts = host.split('.');
  if (parts.length === 2) {
    // Bare registrable apex (e.g. chitdesk.com) — route to the master tenant.
    return `${protocol}//chitos-api.${host}/api`;
  }
  if (parts.length >= 3) {
    const sub = parts[0]!;
    const apex = parts.slice(1).join('.');
    // www is an alias of the apex → master tenant.
    if (sub === 'www') return `${protocol}//chitos-api.${apex}/api`;
    // Skip already-suffixed -api hosts (defensive).
    if (!sub.endsWith('-api')) {
      return `${protocol}//${sub}-api.${apex}/api`;
    }
  }

  // Fallback: same-origin /api
  return `${window.location.origin}/api`;
}

const link = new RPCLink({
  url: resolve_backend_url(),
  fetch: (input, init) =>
    fetch(input, { ...init, credentials: 'include' as const }),
});

export const orpc: RouterClient<AppRouter> = createORPCClient(link);
